import { Button, Popover, Table, Tooltip } from 'antd';
import type { ColumnsType } from 'antd/lib/table';
import { ReactNode, useMemo, useState } from 'react';
import styled from 'styled-components';

import {
  ConsentSignalCellType,
  ConsentSignalColumnType,
  ConsentSignalConsentType,
  ConsentSignalGridType,
  ConsentSignalImpedimentType,
  ConsentSignalRowType,
} from '../../../types';
import { createDateStringPtBr } from '../../../utils/utils';
import {
  CONSENT_LEGEND_ORDER,
  CONSENT_STATUS_CONFIG,
  ConsentSignalIcon,
  consentStatusLabel,
} from './consentSignalStatus';

const SignalWrapper = styled.div`
  /* Mesmo cabeçalho das tabelas internas do sistema (components/antd/Table). */
  .ant-table-thead > tr > th {
    background: #ebf2f7;
    color: var(--ocean_dark);
    font-size: 12px;
    font-weight: 600;
    text-align: center;
    padding: 4px 8px;
  }
  .ant-table-tbody > tr > td {
    padding: 4px 8px;
  }
  .signal-cell {
    text-align: center;
    cursor: default;
  }
  .signal-actions {
    margin-top: 6px;
    font-size: 12px;
  }
`;

const LegendWrapper = styled.div`
  margin-top: 12px;
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
  gap: 4px 16px;
  font-size: 12px;
  .legend-item {
    display: flex;
    align-items: center;
    gap: 8px;
  }
`;

const DetailWrapper = styled.div`
  max-width: 420px;
  font-size: 12px;
  .consent + .consent {
    border-top: 1px solid var(--neutral_lighter);
    margin-top: 8px;
    padding-top: 8px;
  }
  .title {
    font-weight: bold;
  }
  .muted {
    color: var(--neutral_medium);
  }
  ul {
    padding-left: 16px;
    margin: 4px 0 0;
  }
`;

function formatDate(value: string | null) {
  return createDateStringPtBr(value) || '';
}

function ImpedimentDetail({
  impediment,
}: {
  impediment: ConsentSignalImpedimentType;
}) {
  return (
    <li>
      {impediment.consenting_body}: {impediment.reason || 'sem motivo'}{' '}
      <span className="muted">
        ({impediment.active ? 'ativo' : 'encerrado'}
        {impediment.impediment_date
          ? ` desde ${formatDate(impediment.impediment_date)}`
          : ''}
        )
      </span>
    </li>
  );
}

function ConsentDetail({ consent }: { consent: ConsentSignalConsentType }) {
  return (
    <div className="consent">
      <div className="title">
        {consent.consenting_body}
        {consent.consent && consent.consent !== 'Default'
          ? ` - ${consent.consent}`
          : ''}
      </div>
      <div>
        {consent.situation || consentStatusLabel(consent.status)}
        {consent.date ? (
          <span className="muted"> em {formatDate(consent.date)}</span>
        ) : null}
      </div>
      {consent.description_event && consent.description_event.trim() !== '.' && (
        <div className="muted" style={{ whiteSpace: 'pre-line' }}>
          {consent.description_event}
        </div>
      )}
      {consent.requirements.length > 0 && (
        <>
          <div style={{ marginTop: 4 }}>Exigências:</div>
          <ul>
            {consent.requirements.map((requirement, index) => (
              // eslint-disable-next-line react/no-array-index-key
              <li key={index}>
                <span
                  style={{ fontWeight: requirement.open ? 'bold' : 'normal' }}
                >
                  {requirement.name}
                </span>{' '}
                <span className="muted">({requirement.situation})</span>
                {requirement.observation && (
                  <div>{requirement.observation}</div>
                )}
                {requirement.reasons.length > 0 && (
                  <div className="muted">
                    Motivos: {requirement.reasons.join('; ')}
                  </div>
                )}
              </li>
            ))}
          </ul>
        </>
      )}
      {consent.impediments.length > 0 && (
        <>
          <div style={{ marginTop: 4 }}>Impedimentos:</div>
          <ul>
            {consent.impediments.map((impediment, index) => (
              // eslint-disable-next-line react/no-array-index-key
              <ImpedimentDetail key={index} impediment={impediment} />
            ))}
          </ul>
        </>
      )}
    </div>
  );
}

function SignalCell({
  cell,
  row,
}: {
  cell?: ConsentSignalCellType;
  row: ConsentSignalRowType;
}) {
  if (!cell) return null;
  return (
    <Popover
      title={`${row.label} - ${consentStatusLabel(cell.status)}`}
      content={
        <DetailWrapper>
          {cell.consents.map((consent, index) => (
            // eslint-disable-next-line react/no-array-index-key
            <ConsentDetail key={index} consent={consent} />
          ))}
        </DetailWrapper>
      }
    >
      <span>
        <ConsentSignalIcon status={cell.status} showTooltip={false} size={20} />
      </span>
    </Popover>
  );
}

function phaseHeader(
  column: ConsentSignalColumnType,
  grid: ConsentSignalGridType,
  label: string
): ReactNode {
  const phaseStatus = grid.phase_status[column.phase_code];
  if (!phaseStatus) return label;
  const impediments = grid.phase_impediments[column.phase_code] || [];
  return (
    <span>
      {label}{' '}
      <ConsentSignalIcon
        status={phaseStatus}
        size={14}
        tooltip={
          <DetailWrapper>
            <div className="title">{consentStatusLabel(phaseStatus)}</div>
            <ul>
              {impediments.map((impediment, index) => (
                // eslint-disable-next-line react/no-array-index-key
                <ImpedimentDetail key={index} impediment={impediment} />
              ))}
            </ul>
          </DetailWrapper>
        }
      />
    </span>
  );
}

function buildColumns(
  grid: ConsentSignalGridType,
  phaseColumns: ConsentSignalColumnType[]
): ColumnsType<ConsentSignalRowType> {
  const columns: ColumnsType<ConsentSignalRowType> = [
    { title: 'Anuente', dataIndex: 'label', key: 'label', width: 280 },
  ];

  const leaf = (column: ConsentSignalColumnType, label: string) => ({
    title: phaseHeader(column, grid, label),
    key: column.phase_code,
    width: 90,
    className: 'signal-cell',
    render: (_: unknown, row: ConsentSignalRowType) => (
      <SignalCell cell={row.cells[column.phase_code]} row={row} />
    ),
  });

  // Colunas com o mesmo rótulo e sub-rótulo viram um grupo (Operação: Normal e
  // Adicional), como no sinaleiro do PSP.
  phaseColumns.forEach((column) => {
    if (!column.sub_label) {
      columns.push(leaf(column, column.label));
      return;
    }
    const group = columns.find(
      (existing) => existing.key === `group-${column.label}`
    ) as { children: ColumnsType<ConsentSignalRowType> } | undefined;
    if (group) {
      group.children.push(leaf(column, column.sub_label));
    } else {
      columns.push({
        title: column.label,
        key: `group-${column.label}`,
        children: [leaf(column, column.sub_label)],
      });
    }
  });

  return columns;
}

type ConsentSignalTableProps = {
  grid: ConsentSignalGridType;
};

export function ConsentSignalTable({ grid }: ConsentSignalTableProps) {
  const [showAllPhases, setShowAllPhases] = useState(false);

  const extraWithData = grid.extra_columns.filter((column) =>
    grid.rows.some((row) => row.cells[column.phase_code])
  );

  const columns = useMemo(
    () =>
      buildColumns(
        grid,
        showAllPhases ? [...grid.columns, ...grid.extra_columns] : grid.columns
      ),
    [grid, showAllPhases]
  );

  return (
    <SignalWrapper>
      <Table
        size="small"
        bordered
        pagination={false}
        rowKey="key"
        columns={columns}
        dataSource={grid.rows}
        scroll={{ x: 'max-content' }}
        locale={{ emptyText: 'O PSP não devolveu anuências para esta DUV' }}
      />
      <div className="signal-actions">
        <Tooltip
          title={
            extraWithData.length > 0
              ? `Com anuências: ${extraWithData.map((c) => c.label).join(', ')}`
              : 'Sem anuências nas demais etapas'
          }
        >
          <Button
            type="link"
            size="small"
            style={{ padding: 0 }}
            onClick={() => setShowAllPhases((value) => !value)}
          >
            {showAllPhases ? 'Ver menos detalhes' : 'Ver mais detalhes'}
          </Button>
        </Tooltip>
      </div>
    </SignalWrapper>
  );
}

export function ConsentSignalLegend() {
  return (
    <LegendWrapper>
      {CONSENT_LEGEND_ORDER.map((status) => (
        <div className="legend-item" key={status}>
          <ConsentSignalIcon status={status} showTooltip={false} />
          <span>{CONSENT_STATUS_CONFIG[status].label}</span>
        </div>
      ))}
    </LegendWrapper>
  );
}
